import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DEFAULT_LOCALE, type Locale } from "@/i18n";
import type {
  ContentTranslation,
  TranslatableEntityType,
} from "@/i18n/contentTranslations";

/**
 * Fetches all translations for a single entity, batched in one round-trip
 * regardless of how many fields the rendering page needs. Pages call this
 * ONCE per entity, then use `pickLocalized` to extract individual fields.
 *
 * Contract:
 *   - Returns an empty array when `locale === 'sv'` (the default render path
 *     does not need translations).
 *   - Returns an empty array when `entityId` is falsy — safe to call before
 *     parent data has loaded.
 *   - RLS does the security filtering: anon receives only approved+parent-
 *     public rows; the rendering layer does not re-check status.
 *
 * See `docs/SoT/localization.md` §5.5.
 */
export function useEntityTranslations(
  entityType: TranslatableEntityType,
  entityId: string | null | undefined,
  locale: Locale,
): UseQueryResult<ContentTranslation[]> {
  const enabled = Boolean(entityId) && locale !== DEFAULT_LOCALE;

  return useQuery<ContentTranslation[]>({
    queryKey: ["content_translations", entityType, entityId ?? null, locale],
    enabled,
    // Stay close to the project-wide default (30s). Translations change
    // rarely — bumping this is fine if/when we see request volume.
    staleTime: 30_000,
    queryFn: async () => {
      if (!entityId) return [];
      const { data, error } = await supabase
        .from("content_translations")
        .select(
          "id, entity_type, entity_id, field_name, locale, source_locale, source_hash, value, status, generated_by_ai, created_at, updated_at, updated_by",
        )
        .eq("entity_type", entityType)
        .eq("entity_id", entityId)
        .eq("locale", locale);

      if (error) {
        // RLS denials surface as 0 rows, not as errors, so a non-null error
        // here is a real fault we want to log/throw.
        throw error;
      }

      return (data ?? []) as ContentTranslation[];
    },
  });
}
